/**
 * The page shown on a successful outcome
 */
import * as React from 'react';
import * as gls from '../../ap/layout/gls';
import * as styles from '../../ap/base/styles';
import { Heading3, Heading4, Paragraph, Ul } from '../../ap/base/text';
import { Done } from '../../ap/base/icons';
import { Page } from '../base/page';
import { Hr } from '../../ap/base/hr';
import { SectionLoader, Loader, ProgressBar } from '../../ap/base/loader';
import { ActionAnchor, AnchorStyles } from '../../ap/base/anchor';
import { MaxWidthIsland, FormIsland } from '../../ap/pages/sections';
import { DocumentSightingStep, DocumentSightingStepper } from '../components/appStepper';
import { PrimaryButton } from '../../ap/base/button';
import * as text from '../../ap/base/text';
import * as links from '../../links';
import { FieldState } from '../../ap/fields/field';
import { Dates } from '../../utils/formatters';
import { DocumentImageDropzone, DropzoneLikeImageFileDisplay } from './documentImageDropzone';
import { docs } from '../../links';
import { Anchor } from '../../ap/base/anchor';
import { SecondaryButton } from '../../ap/base/button';
import { DisplayValue, DisplayValueSelectField } from '../../ap/fields/selectField';
import * as alerts from '../../ap/base/alerts';
import * as utils from '../../ap/utils/utils';
import { uploadFile } from '../components/dropzone';
import * as validation from '../../ap/fields/validation/validation';
import * as docManagerService from '../../webServices/docManagerService';
import { appState } from '../../state/state';
const {voi} = appState;
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { style } from 'typestyle';
import * as csx from 'csstips';
import {authenticationService} from "../../webServices/authenticationService";


import {
  CategoryName, CategoryNameToDisplay, documentSightingCategories,
  DocumentName, DocumentNameToDisplay
}
  from './documentSightingConfig';

import {CreateOrderQuery} from "../../webServices/docManagerService";
import {OrderFile} from "../../webServices/docManagerService";

import { trackDocumentSightingPage, trackLinkClick } from '../../ap/omniture/omnitureService';

/**
 * Powers the category drop down
 */
const categories: DisplayValue[] = Object.keys(CategoryNameToDisplay).map(categoryNameKey => {
  return {
    display: CategoryNameToDisplay[categoryNameKey],
    value: CategoryName[categoryNameKey]
  }
});

/**
 * Good enough for the document numbers we have
 */
export const numberToDisplay = (x: number) => {
  return {
    '1': "One",
    '2': "Two",
    '3': "Three",
    '4': "Four",
    '5': "Five",
    '6': "Six",
    '7': "Seven",
    '8': "Eight",
    '9': "Ten",
  }[x];
}

interface DocumentSightingPageProps {
}

type CategorySubSectionImageState = {
  /** Till the file is uploaded */
  localDocumentId: string,
  /** once the file is uploaded */
  serverDocumentId?: string,
  documentType: DocumentName,
  file: File,
  fileUploadUrl?: string,
  uploadStatus: "local" | "uploading" | "uploaded" | "failed",
  uploadCategory: "image" | "form"
  uploadProgress: number
}

type CategorySubSectionsState = {
  documentNames: DocumentName[],
  documentSelectField: FieldState<DisplayValue>,
  documentSelectFieldValues: DisplayValue[],
  images: CategorySubSectionImageState[]
}

type CategoryState = {
  subSections: CategorySubSectionsState[],
  otherImages: CategorySubSectionImageState[],
}

export class DocumentSightingPage extends React.Component<DocumentSightingPageProps, {}> {
  @observable categorySelectField?: FieldState<DisplayValue> = undefined;

  /**
   * We reinitialize this state if the user changes the category
   */
  @observable categoryState?: CategoryState = undefined;

  /**
   * If something is invalid
   */
  @observable invalidMessage?: string = undefined;

  /**
   * When uploading images we pull this out of categories into a local position
   */
  @observable currentUploadInformation?: {
    imagesToUpload?: CategorySubSectionImageState[];
    doneCount: number,
    totalCount: number,
    /** Takes done / total count + current image upload progress into account */
    overallImageUploadProgress: number,
  } = undefined;

  // MobX strict-mode (enforceActions) requires observable mutations to occur inside actions.
  @action.bound
  clearImageInvalid() {
    this.invalidMessage = null;
  }

  constructor(props) {
    super(props);
  // Required for MobX 6 when using @observable decorators
  makeObservable(this);
    let selectedCategory = categories[0];

    const initializeCategorySubState = (selectedCategoryNameString: string): CategoryState => {
      const categoryName = CategoryName[selectedCategoryNameString];
      const categoryConfig = documentSightingCategories.find(c => c.categoryName == categoryName);

      const categoryState: CategoryState = {
        subSections: categoryConfig.subSections.map((s, i) => {
          const documentSelectFieldValues = s.documentNames.map(documentId => {
            return {
              display: DocumentNameToDisplay[documentId],
              value: DocumentName[documentId]
            }
          });

          const categorySubSectionState: CategorySubSectionsState = {
            documentNames: s.documentNames,
            documentSelectField: new FieldState<DisplayValue>({
              value: documentSelectFieldValues[0],
              onUpdate: (documentSelectField) => {
                action(() => {
                  // Re init categorySubSectionState.
                  // Primarily clear all images
                  this.categoryState.subSections[i] = {
                    documentSelectField,
                    documentNames: s.documentNames,
                    documentSelectFieldValues,
                    images: []
                  }
                  this.clearImageInvalid();
                })();
                this.forceUpdate();
              }
            }),
            documentSelectFieldValues,
            images: [],
          }
          return categorySubSectionState;
        }),
        /** Preserve otherImages if any */
        otherImages: this.categoryState && this.categoryState.otherImages ? this.categoryState.otherImages : [],
      }

      return categoryState;
    }

    this.categorySelectField = new FieldState<DisplayValue>({
      value: selectedCategory,
      onUpdate: (newCategorySelectField) => {
        action(() => {
          const newSelectedCategoryName = newCategorySelectField.value.value;
          this.categoryState = initializeCategorySubState(newSelectedCategoryName);
          this.clearImageInvalid();
          this.categorySelectField = newCategorySelectField;
        })();
      }
    });
    this.categoryState = initializeCategorySubState(selectedCategory.value);
  }

  getSelectedCategoryConfig = () => {
    const categoryName = CategoryName[this.categorySelectField.value.value]
    return documentSightingCategories.find(c => c.categoryName == categoryName);
  }
  private goBack(){
    links.navigateTo(links.route.registerEdit(), true);
  }

  componentDidMount() {
    trackDocumentSightingPage();
  }

  render() {
    const omnitureOnClickViewCategoryAndOptions = () => trackLinkClick('diy register','document sighting:view category and options');

    const sections = [
      <MaxWidthIsland>
        <DocumentSightingStepper current={DocumentSightingStep.DocumentSighting} />
      </MaxWidthIsland>,
      <FormIsland>
        <Heading4 text="Select a category and provide relevant documents" />
        <gls.SmallVerticalSpace space={styles.spacing.verticalBetweenFields} />
        <gls.ContentHorizontalCentered>
          <DisplayValueSelectField
            id="selectCategoryDropdown"
            fieldState={this.categorySelectField}
            values={categories} />
          <gls.SmallHorizontalSpace space={styles.spacing.horizontalBetweenFields} />
          <Anchor href={docs.categoriesAndOptions()} target="_blank" onClick={omnitureOnClickViewCategoryAndOptions}>
            <span>View categories and options</span>
          </Anchor>
        </gls.ContentHorizontalCentered>
        <gls.SmallVerticalSpace space={styles.spacing.verticalBetweenFields} />
        <Heading4 text={`${this.categorySelectField.value.display}: ${numberToDisplay(this.getSelectedCategoryConfig().subSections.length)} documents in total`} />
        <gls.SmallVerticalSpace space={styles.spacing.verticalBetweenFields} />
        <Ul>
          <React.Fragment>
            {this.getSelectedCategoryConfig().subSections.map((s, i) => (
              <li key={i}>{this.renderDocumentOptionsText(s.documentNames)}</li>
            ))}
          </React.Fragment>
        </Ul>
      </FormIsland>,
      ...this.renderDocumentSections(),
      <FormIsland key={"documentSection-other"}>
        <gls.ContentVerticalContentMargined margin={styles.spacing.verticalBetweenFields}>
          <gls.ContentVerticalContentMargined margin={10}>
            <Paragraph>
              <span><strong>ADD where applicable</strong>: Additional supporting documentation (Optional)</span>
            </Paragraph>
              <Paragraph>
                <span>
                  {/*<div className={style(csx.padding(0,0,0,10))}>*/}
                  e.g. Change of name linking documents, Client Authorisation Form, Supporting Land Registry Forms, Passport style photo / images of Person Being Identified
                  {/*</div>*/}
                </span>
              </Paragraph>
          </gls.ContentVerticalContentMargined>
          {
            this.categoryState.otherImages.map(
              (image) =>
                <DropzoneLikeImageFileDisplay
                  key={`other-${image.localDocumentId}`}
                  file={image.file}
                  onRemove={() => {
                    action(() => {
                      this.categoryState.otherImages = this.categoryState.otherImages.filter(img => img !== image);
                      this.clearImageInvalid();
                    })();
                    this.forceUpdate();
                  } }
                  progress={image.uploadStatus === "uploading" ? image.uploadProgress : 0}
                  uploaded={image.uploadStatus === "uploaded"}
                  errorMessage={image.uploadStatus === "failed" ? "Failed to upload image" : ""} />
            )
          }
          <DocumentImageDropzone
            transactionId={voi.transactionId}
            text={this.categoryState.otherImages.filter(x => x.uploadStatus !== "failed").length > 0 ? "Add another (Optional)" : "Add (Optional)"}
            onAdd={({file}) => {
              action(() => {
                this.categoryState.otherImages.push({
                  localDocumentId: utils.createGuid(),
                  documentType: DocumentName.other_supporting_document,
                  file,
                  uploadStatus: 'local',
                  uploadCategory: 'form',
                  uploadProgress: 0,
                })
                this.clearImageInvalid();
              })();
            } } />
        </gls.ContentVerticalContentMargined>
      </FormIsland>,
      <FormIsland>
        {this.renderLegalText()}
        <gls.SmallVerticalSpace space={styles.spacing.verticalBetweenFields} />
        {
          !!this.invalidMessage && <div>
            <alerts.Alert type={alerts.AlertType.Error}>
              {this.invalidMessage}
            </alerts.Alert>
            <gls.SmallVerticalSpace space={styles.spacing.verticalBetweenFields} />
          </div>
        }
        {
          (!!this.currentUploadInformation && !!this.currentUploadInformation.imagesToUpload && !!this.currentUploadInformation.imagesToUpload.length)
            ? <gls.ContentHorizontalCentered>
              <gls.Flex>
                <ProgressBar percent={this.currentUploadInformation.overallImageUploadProgress} />
              </gls.Flex>
              <gls.SmallHorizontalSpace />
              <Paragraph>
                <span>Uploading {this.currentUploadInformation.doneCount + 1}/{this.currentUploadInformation.totalCount}</span>
              </Paragraph>
              <Paragraph>
                <span>Please note: If you are unable to provide the required documents, please contact us for further guidance.</span>
              </Paragraph>
            </gls.ContentHorizontalCentered>
            : <gls.ContentHorizontal>
              <a href='' onClick={this.goBack}>Back</a>
              <gls.Flex />
              <PrimaryButton id="submitButton" text="Submit" onClick={this.onSubmit} disabled={!!this.invalidMessage} />
            </gls.ContentHorizontal>
        }
      </FormIsland>
    ]

    return (
      <Page
        loggedIn={true}
        sections={sections}
        />
    );
  }

  buildOrderFiles = (localImageInfos: CategorySubSectionImageState[]) =>  {

    let orderFiles = new Array<OrderFile>();

    localImageInfos.forEach(localImageInfo => {
      orderFiles.push({
        fileName: localImageInfo.file.name,
        fileId: localImageInfo.localDocumentId + localImageInfo.file.name,
        documentType: localImageInfo.documentType.toString().toUpperCase()
      })
    });

    return orderFiles;
  }

  buildOrder = (localImageInfos: CategorySubSectionImageState[]) => {
    let theOrder: CreateOrderQuery = {
      firstName: voi.firstName.value,
      middleNames: voi.middleNames.value,
      lastName: voi.surname.value,
      dateOfBirth: voi.dob.value,
      phoneNumber: voi.verifiedPhoneNumber.value,
      email: voi.email.value,
      addressLine1: voi.addressLine1.value,
      addressLine2: voi.addressLine2.value,
      suburb: voi.suburb.value,
      state: voi.state.value.value,
      postcode: voi.postcode.value,
      dpid: voi.dpid.value,
      transactedAddress: voi.propertyBeingTransacted.value,
      referenceId: voi.transactionId,
      category: voi.categorySelectField,
      orderFiles: this.buildOrderFiles(localImageInfos)
    };

    return theOrder;
  }

  updateProgressFileUploading = (progress) => {
    if (!!this.currentUploadInformation) {
      const lastState = this.currentUploadInformation;

      const completedFilesProgress = (lastState.doneCount  / lastState.totalCount) * 100;
      const currentFileProgress = progress / lastState.totalCount;
      const overallProgress = completedFilesProgress + currentFileProgress;

      action(() => {
        this.currentUploadInformation.overallImageUploadProgress = overallProgress;
      })();
    }
  }

  updateProgressFileUploadComplete = () => {
    if (!!this.currentUploadInformation) {
      const lastState = this.currentUploadInformation;

      /** Overall progress */
      const eachFileGets = 1 / lastState.totalCount;
      const doneCount = Math.min(lastState.doneCount + 1, lastState.totalCount - 1);
      const overallProgress = Math.min((lastState.doneCount + 1) * eachFileGets * 100, 100);

      action(() => {
        this.currentUploadInformation.overallImageUploadProgress = overallProgress;
        this.currentUploadInformation.doneCount = doneCount;
      })();
    }
  }

  onSubmit = () => {
    const error = this.missingImagesError();
    voi.categorySelectField = this.categorySelectField.value.display;
    if (error) {
      action(() => {
        this.invalidMessage = error;
      })();
      return;
    }

    /** If there are any images that are local note them down for upload */
    let imagesToUpload: CategorySubSectionImageState[] = [];

    const collectLocalImagesForUpload = (images: CategorySubSectionImageState[]) => {
      images
        .filter(image => image.uploadStatus === 'local')
        .forEach(localImage => imagesToUpload.push(localImage));
    }

    /** Category images */
    this.categoryState.subSections.forEach(s => {
      collectLocalImagesForUpload(s.images);
    });

    /** caf images */
    collectLocalImagesForUpload(this.categoryState.otherImages);

    /** The overall info */
    action(() => {
      this.currentUploadInformation = {
        imagesToUpload,
        doneCount: 0,
        totalCount: imagesToUpload.length,
        overallImageUploadProgress: 0,
      };
    })();

    const fail = (failedImage) => {
      action(() => {
        failedImage.uploadStatus = "failed";
        this.currentUploadInformation = undefined;
        this.invalidMessage = "Failed to upload an image. Please remove and try another one.";
      })();
    };

    const uploadImages = (images: CategorySubSectionImageState[]) => {
      const [firstImage, ...remainingImages] = images;
      uploadFile({
        file: firstImage.file,
        url: firstImage.fileUploadUrl,
        onFail: () => {
          fail(firstImage);
        },
        onProgress: (progress) => {
          action(() => {
            firstImage.uploadStatus = "uploading";
            firstImage.uploadProgress = progress;
          })();

          this.updateProgressFileUploading(progress);
        },
        onSuccess: () => {
          action(() => {
            firstImage.uploadStatus = "uploaded";
          })();

          this.updateProgressFileUploadComplete();

          const allImagesUploaded = remainingImages.length === 0;
          if (allImagesUploaded) {
            links.navigateTo(links.route.payment());
          } else {
            uploadImages(remainingImages);
          }
        }
      })
    };

    const categoryNameAsString = this.categorySelectField.value.value;
    const categoryName = CategoryName[categoryNameAsString];

    if (imagesToUpload.length) {
      docManagerService.createOrder(this.buildOrder(imagesToUpload))
        .then((res) => {
          voi.orderId = res.ptOrderId;
          voi.securepayCustomerId = res.securepayCustomerId;
          voi.securepayAccessToken = res.securepayAccessToken;

          // Update the local image information with the details of the URL to which the file will be uploaded.
          const guidImageMap = {};
          imagesToUpload.map((imageToUpload, index) => {
            guidImageMap[imageToUpload.localDocumentId + imageToUpload.file.name] = imageToUpload;
          });

          // From the response, feed the S3 URL back into each respective local image info.
          res.orderFiles.map((orderFile, index) => {
            const localImage = guidImageMap[orderFile.fileId];

            if (!!localImage) {
              localImage.fileUploadUrl = orderFile.fileUploadUrl;
            }
          });

          // Double-check to make sure all images received an upload URL from the API.
          imagesToUpload.map((imageToUpload, index) => {
            if (!imageToUpload.fileUploadUrl || imageToUpload.fileUploadUrl.length === 0) {
              throw new Error('Failed to retrieve an upload destination for \'' + imageToUpload.file.name + '\'');
            }
          });

          // Use the recursive function...
          uploadImages(imagesToUpload);
        })
        .catch((err) => {
          action(() => {
            this.currentUploadInformation = undefined;
            this.invalidMessage = "Failed to upload some or all of the images. Please check and try again.";
          })();
          throw err;
        });
    }
  }

  missingImagesError = () => {
    const missingImages = this.categoryState.subSections.some(s => s.images.length === 0);
    // Potentially highlight the ones that have images missing as well
    // If we do then we would have to clear them accordingly
    if (missingImages) {
      return "You must provide an image for each document in order to submit.";
    }

    // the whacky code below is how to deal with the whacky ObservableArray multi-level data type in subSections
    // for some reason .map() returns an array of array of boolean values, if any of these values are "true"
    // it means there is an image with size 0. The alternative is a multi step .flatMap()/.subscribe()/.filter()
    // if you figure it out please change the below!
    const zeroImageSizes = JSON.stringify(this.categoryState.subSections
      .map(s => s.images.map(t => t.file.size === 0)))
      .includes("true");
    if (zeroImageSizes) {
      return "All images must have a file size greater than 0 bytes.";
    }
  }

  renderDocumentSections() {
    const config = this.categoryState;
    return config.subSections.map((s, i) => {
      return (
        <FormIsland key={"documentSection-" + i}>
          <gls.ContentVerticalContentMargined margin={styles.spacing.verticalBetweenFields}>
            <Paragraph>
              <span><strong>{i !== 0 ? "AND " : ""}</strong>{this.renderDocumentOptionsText(s.documentNames)}</span>
            </Paragraph>
            <Paragraph>
              <span>If you are unable to provide the required documents, please contact us for further guidance.</span>
            </Paragraph>
            <DisplayValueSelectField
              id={"selectIdTypeDropdown" + i}
              label="Select ID Type"
              fieldState={s.documentSelectField}
              values={s.documentSelectFieldValues}
            />
            {
              s.images.map(
                (image) =>
                  <DropzoneLikeImageFileDisplay
                    key={`sub-${i}-${image.localDocumentId}`}
                    file={image.file}
                    onRemove={() => {
                      action(() => {
                        s.images = s.images.filter(img => img !== image);
                        this.clearImageInvalid();
                      })();
                      this.forceUpdate();
                    } }
                    progress={image.uploadStatus === "uploading" ? image.uploadProgress : 0}
                    uploaded={image.uploadStatus === "uploaded"}
                    errorMessage={image.uploadStatus === "failed" ? "Failed to upload image" : ""} />
              )
            }
            <DocumentImageDropzone
              id={"fileInput" + i}
              transactionId={voi.transactionId}
              text={s.images.filter(x => x.uploadStatus !== "failed").length > 0 ? "Add another (optional)" : "Add"}
              onAdd={({file}) => {
                action(() => {
                  s.images.push({
                    localDocumentId: utils.createGuid(),
                    documentType: DocumentName[s.documentSelectField.value.value],
                    file,
                    uploadStatus: 'local',
                    uploadCategory: 'form',
                    uploadProgress: 0,
                  })
                  this.clearImageInvalid();
                })();
                this.forceUpdate();
              } } />
          </gls.ContentVerticalContentMargined>
        </FormIsland>
      );
    })
  }

  renderDocumentOptionsText(documentNames: DocumentName[]) {
    return <span>
      <strong>ONE</strong> of {
        documentNames.map(s => DocumentNameToDisplay[s]).reduce(
          (acc, v, i, arr) => (i === (arr.length - 1)) ? acc + ' or ' + v : acc + ', ' + v
        )
      }
    </span>;
  }

  renderLegalText = () => {

    // Don't allow the name to break over two lines.
    let nonBreakingSpace = "\u00a0";
    let regExpContiguousWhitespace = /\s+/g;
    const client = `${voi.firstName.value} ${voi.middleNames.value} ${voi.surname.value}`.trim().replace(regExpContiguousWhitespace, nonBreakingSpace);
    const practitioner = authenticationService.loadUserDetails().name;
    const today = Dates.happyDateLocal(new Date());

    return (
      <gls.ContentVerticalContentMargined margin={10}>
        <Heading4 text={`${this.categorySelectField.value.display} VOI submission`} />

        <text.Paragraph>
          <span>I "{practitioner}", have conducted a verification of identity of {client} on {today} in accordance with the VOI Standard* and hereby certify:</span>
        </text.Paragraph>
        <text.Paragraph>
          <span>(a) the verification of identity of {client} was conducted during a face-to-face in-person interview;</span>
        </text.Paragraph>
        <text.Paragraph>
          <span>(b) I am satisfied that there was a reasonable likeness of {client} to the person depicted in photographs contained in the presented identification documents;</span>
        </text.Paragraph>
        <text.Paragraph>
          <span>(c) the presented identification documents were originals and satisfy one category of the VOI Standard* minimum document requirements for verification of identity;</span>
        </text.Paragraph>
        <text.Paragraph>
          <span>(d) all of the presented identification documents were current, except for (if applicable) an expired Australian Passport which has not been cancelled and was current within the preceding 2 years; and</span>
        </text.Paragraph>
        <text.Paragraph>
          <span>(e) I have retained copies of all the presented identification documents.</span>
        </text.Paragraph>

        <text.Paragraph />
        <text.Paragraph>
          <em>
            <span>* "VOI Standard" means the Australian Registrars National Electronic Conveyancing Council’s Verification of Identity Standard, contained in the Model Participation Rules Version 4 (published May 2017 and available at </span>
            <Anchor href="https://www.arnecc.gov.au" target="_blank"><span>www.arnecc.gov.au</span></Anchor>
            <span>)</span>
          </em>
        </text.Paragraph>
      </gls.ContentVerticalContentMargined>
    );
  }
}

// Export an observer-wrapped component so the component reacts to its @observable fields
export default observer(DocumentSightingPage as any) as typeof DocumentSightingPage;
